'use client' 
import Cta from "@/components/Cta";
import PropertyCard from "@/components/PropertyCard";
import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import { Children, cloneElement, isValidElement, useEffect, useState } from "react";
import '@splidejs/react-splide/css/core';
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "./carrousel-slider.css";
import { Variation } from "@/script/constant";
import { docToCardItem } from "@/script/propertyDocument";

/**
 * @typedef {import("@prismicio/client").Content.CarouselSlice} CarouselSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<CarouselSlice>} CarouselProps
 * @type {import("react").FC<CarouselProps>}
 */

const renderWithEmojiSvg = (children, keyPrefix = "emoji") => {
  const emojiPattern =
    /\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?)*?/gu;

  const parseText = (text, prefix) => {
    const matches = [...text.matchAll(emojiPattern)];
    if (matches.length === 0) return text;

    const nodes = [];
    let lastIndex = 0;

    matches.forEach((match, index) => {
      const emoji = match[0];
      const start = match.index ?? 0;

      if (start > lastIndex) {
        nodes.push(text.slice(lastIndex, start));
      }

      nodes.push(
        <span key={`${prefix}-${index}`} aria-hidden="true" className="inline-flex align-middle -mt-2">
          <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="animate-va-et-vient inline-block">
            <path fillRule="evenodd" clipRule="evenodd" d="M15.7742 2.01518C17.2485 1.70148 18.9584 1.75583 20.8148 2.9183C22.4771 3.95951 24.1512 5.84301 25.1624 8.13646C25.8868 9.77994 26.2327 11.5655 26.3508 13.3242C27.4881 12.4371 28.5589 11.9493 29.4379 11.6484C31.1994 11.0456 34.7004 10.0311 37.6955 10.5056C39.5954 10.8066 40.543 11.0667 41.592 11.3562L41.9382 11.4506L42.0131 11.4733C42.0878 11.4965 42.1625 11.523 42.2343 11.5538C42.5376 11.6835 42.7853 11.788 42.992 11.8752C43.4666 12.0752 43.9751 12.2742 44.4123 12.5767L44.4986 12.6381L44.5789 12.7C44.7621 12.8444 44.9141 12.9911 44.9942 13.067C45.0391 13.1095 45.0876 13.1554 45.1422 13.2061C45.2134 13.2367 45.2847 13.2679 45.3533 13.3003C45.483 13.3617 45.7913 13.5109 46.0963 13.7505C46.1131 13.7638 46.1308 13.7772 46.1473 13.7909L46.1877 13.825C46.3003 13.919 46.4041 14.0244 46.496 14.138L46.931 14.6765C47.2947 15.0531 47.6416 15.5462 47.7938 16.1982C47.9359 16.8076 47.8447 17.3812 47.8293 17.5129C47.8147 17.637 47.8049 17.7226 47.8006 17.8067L47.7985 17.8923L47.7934 18.1123C47.7554 19.2187 47.523 20.4384 47.2825 21.5764L47.2514 21.7156C46.9458 22.953 46.1408 23.7988 45.3867 24.544L45.0568 24.8696L45.0154 24.9114C43.8766 26.0423 41.8948 27.8789 40.3149 29.2395C37.6035 31.5745 35.795 32.6703 34.2586 33.554C32.2637 34.7013 30.7984 35.6908 30.1124 36.1538C30.0083 36.2241 29.9109 36.2893 29.8347 36.3399C28.9968 36.896 27.9906 37.3543 27.0084 37.8017L26.8236 37.8855C25.8271 38.3391 24.6628 38.8692 23.2284 39.6138C22.5253 39.9788 21.8251 40.328 21.1989 40.6403C20.3183 41.0794 19.6311 41.4229 19.1876 41.6749C19.1812 41.6788 19.1459 41.7015 19.0793 41.7517C19.0063 41.8069 18.9164 41.8794 18.8091 41.9715C18.5894 42.1602 18.352 42.3818 18.088 42.628C17.9444 42.7619 17.7796 42.9151 17.6131 43.0652C17.4224 43.3372 17.2057 43.529 17.0723 43.6358C16.977 43.7121 16.8564 43.7993 16.7132 43.8818C16.5909 43.9521 16.3453 44.0815 16.0155 44.1561C15.9621 44.1682 15.8847 44.1839 15.7895 44.1957C14.8063 44.4523 13.7094 44.1681 12.977 43.3702C12.8317 43.2117 12.7075 43.0406 12.6062 42.862C12.1096 42.2542 12.1082 41.5516 12.1493 41.1989C10.6623 34.5778 9.41652 27.7618 8.47195 22.5958C8.10464 20.587 7.78417 18.8352 7.51161 17.4311C7.02928 14.9463 6.78163 13.3076 6.70922 12.1505L6.69646 11.9255C6.63624 10.5858 6.82867 9.79078 7.16091 9.01835C8.13324 6.75846 10.1294 5.02711 11.9142 3.71934C13.1106 2.84292 14.3979 2.33267 15.6329 2.04764L15.7742 2.01518Z" fill="#D96545" />
          </svg>
          <span className="sr-only">{emoji}</span>
        </span>,
      );

      lastIndex = start + emoji.length;
    });

    if (lastIndex < text.length) {
      nodes.push(text.slice(lastIndex));
    }

    return nodes;
  };

  const parseNode = (node, prefix) => {
    if (typeof node === "string") {
      return parseText(node, prefix);
    }

    if (Array.isArray(node)) {
      return node.map((child, index) => parseNode(child, `${prefix}-${index}`));
    }

    if (isValidElement(node) && node.props?.children) {
      return cloneElement(node, {
        ...node.props,
        children: parseNode(node.props.children, `${prefix}-child`),
      });
    }

    return node;
  };

  return Children.map(children, (child, index) =>
    parseNode(child, `${keyPrefix}-${index}`),
  );
};

function normalizeTag(tag) {
  return tag
    .replace(/œ/g, "oe")
    .replace(/Œ/g, "oe")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function hasCoupDeCoeurTag(entry) {
  const tags = entry?.tags ?? entry?.property?.tags;
  if (!Array.isArray(tags)) return false;
  return tags.some(
    (tag) => typeof tag === "string" && normalizeTag(tag) === "coup de coeur"
  );
}

function getNormalizedState(entry) {
  const rawState = entry?.data?.state ?? entry?.property?.data?.state;
  return typeof rawState === "string" ? normalizeTag(rawState) : "";
}

function getCardItem(entry) {
  if (entry?.property) return entry;
  return docToCardItem(entry);
}

function getCardKey(entry, fallbackPrefix, index) {
  return (
    entry?.id ??
    entry?.property?.id ??
    entry?.property?.uid ??
    entry?.uid ??
    `${fallbackPrefix}-${index}`
  );
}

const MAX_CAROUSEL_RESULTS = 9;

const Carousel = ({ slice }) => {
  const [dynamicProperties, setDynamicProperties] = useState([]);

  const isDefaultVariation = slice.variation === "default";
  const isCarouselLightVariation = slice.variation === "carouselLight";
  const isCarouselRentedVariation = slice.variation === "carouselRented";

  useEffect(() => {
    let cancelled = false;

    async function loadProperties() {
      try {
        const documentType = isCarouselRentedVariation ? "property_rental" : "property";
        const response = await fetch(`/api/properties?type=${documentType}`);
        if (!response.ok) {
          throw new Error("Impossible de charger les biens du carousel.");
        }
        const data = await response.json();
        const source = Array.isArray(data.documents) ? data.documents : [];
        const filtered = source.filter((property) => {
          if (!hasCoupDeCoeurTag(property)) {
            return false;
          }

          const state = getNormalizedState(property);
          if (isDefaultVariation) {
            return state !== "vendu";
          }

          if (isCarouselLightVariation) {
            return state === "vendu";
          }

          if (isCarouselRentedVariation) {
            return state === "loue";
          }

          return true;
        });

        const limited = filtered.slice(0, MAX_CAROUSEL_RESULTS);

        if (!cancelled) {
          setDynamicProperties(limited);
        }
      } catch (error) {
        if (!cancelled) {
          setDynamicProperties([]);
        }
      }
    }

    loadProperties();

    return () => {
      cancelled = true;
    };
  }, [isCarouselLightVariation, isCarouselRentedVariation, isDefaultVariation]);

  const properties = dynamicProperties;

  const showMobileSlider = properties.length > 0;
  const showTabletSlider = properties.length > 2;
  const showDesktopSlider = properties.length >= 4;
  const showTabletGrid = properties.length > 0 && properties.length <= 2;
  const showDesktopGrid = properties.length > 0 && properties.length < 4;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`contre-uno ${slice.variation === "carouselRented" ? "bg-terracota-400 text-terracota-100" : "bg-bleu-800 text-bleu-100"} rounded-t-4xl lg:rounded-t-[3.7rem] -mt-14`}
    >
      <div className="padding-y-rounded flex flex-col gap-10 lg:gap-18">
        {isFilled.richText(slice.primary.title) && (
          <div className="padding-x-sm framing">
            <PrismicRichText
              field={slice.primary.title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="h3-h2 text-center md:text-start md:pr-40 ">{renderWithEmojiSvg(children, "heading2")}</h2>
                ),
                heading3: ({ children }) => (
                  <h3 className="h3-h2 text-center md:text-start md:pr-40 ">{renderWithEmojiSvg(children, "heading3")}</h3>
                ),
              }}
            />
          </div>
        )}

        <>
          {showMobileSlider && (
            <div className="md:hidden">
              <div className="px-3 gap-4 flex snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] overflow-x-scroll">
                {properties.map((item, index) => (
                  <div
                    key={getCardKey(item, "mobile-property", index)}
                    className="snap-center snap-always flex-shrink-0 w-[85vw] max-w-[360px]"
                  >
                    <PropertyCard item={getCardItem(item)} variation={slice.variation === "default" ? Variation.DISPONIBLE : Variation.NOT_DISPONIBLE}/>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showDesktopSlider && (
            <div className="hidden xl:block overflow-visible">
              <Splide
                options={{
                  perPage: 3,
                  perMove: 3,
                  gap: "2.12rem",
                  pagination: false,
                  arrows: true,
                  width: "100%",
                  padding: "4rem",
                }}
                aria-label="Biens immobiliers"
              >
                {properties.map((item, index) => (
                  <SplideSlide key={getCardKey(item, "desktop-slider-property", index)} className="group overflow-visible">
                    <PropertyCard item={getCardItem(item)} variation={slice.variation === "default" ? Variation.DISPONIBLE : Variation.NOT_DISPONIBLE}/>
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          )}

          {showTabletSlider && (
            <div className="hidden md:block xl:hidden overflow-visible ">
              <Splide
                options={{
                  perPage: 2,
                  perMove: 2,
                  gap: "1rem",
                  pagination: false,
                  arrows: true,
                  width: "100%",
                  padding: "1.5rem",
                }}
                aria-label="Biens immobiliers"
              >
                {properties.map((item, index) => (
                  <SplideSlide key={getCardKey(item, "tablet-slider-property", index)} className="group overflow-visible">
                    <PropertyCard item={getCardItem(item)} variation={slice.variation === "default" ? Variation.DISPONIBLE : Variation.NOT_DISPONIBLE}/>
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          )}

          {showDesktopGrid && (
            <div className={`hidden xl:flex flex-row justify-center gap-[2.12rem] padding-x-sm`}>
              {properties.map((item, index) => (
                <div key={getCardKey(item, "desktop-grid-property", index)} className={`group w-1/3`}>
                  <PropertyCard item={getCardItem(item)} variation={slice.variation === "default" ? Variation.DISPONIBLE : Variation.NOT_DISPONIBLE}/>
                </div>
              ))}
            </div>
          )}

          {showTabletGrid && (
            <div className="hidden md:flex xl:hidden flex-row justify-center gap-4 padding-x-sm">
              {properties.map((item, index) => (
                <div key={getCardKey(item, "tablet-grid-property", index)} className="group w-1/2">
                  <PropertyCard item={getCardItem(item)} variation={slice.variation === "default" ? Variation.DISPONIBLE : Variation.NOT_DISPONIBLE} />
                </div>
              ))}
            </div>
          )}
        </>
        {isFilled.link(slice.primary.cta) && slice.variation === "default" && (
          <Cta color="bleu-50" justify="justify-center" field={slice.primary.cta} />
        )}
      </div>

    </section>
  );
};

export default Carousel;
